import { Box, Grid, IconButton, Tooltip } from '@mui/material'
import { Control, FieldValues, Path, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import FormInput from 'components/FormInput'
import FormInputDatePicker from 'components/FormInputDatePicker'
import { FormInputAutoComplete } from 'components/FormInputAutoComplete'
import { ItemOption } from 'components/InputSelect'
import { FormAddress } from 'components/FormAddress'
import React, { useEffect, useState } from 'react'
import { phoneMask } from 'utils/mask.util'
import { FieldPathValue } from 'react-hook-form/dist/types/path'
import { FieldPath } from 'react-hook-form/dist/types/path/eager'
import DeleteIcon from '@mui/icons-material/Delete'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import DialogAlert from 'components/Dialog/Alert'

interface FamiliarCompositionItemProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  watch: UseFormWatch<TFieldValues>
  setValue: UseFormSetValue<TFieldValues>
  name: Path<TFieldValues>

  index: number
  disabled?: boolean
  showDeleteButton: boolean

  hasAddress?: boolean
  onDelete: (index: number) => void

  familiarReferenceOptions: ItemOption[]
  labels: {
    labelDeleteItem: string
  }
}

export const FamiliarCompositionItem = <TFieldValues extends FieldValues>({
  control,
  name,
  index,
  disabled,
  setValue,
  watch,

  showDeleteButton,
  onDelete,

  labels,

  hasAddress,
  familiarReferenceOptions,
}: FamiliarCompositionItemProps<TFieldValues>) => {
  const [openExcluded, setOpenExcluded] = useState<boolean>(false)

  const nameIndex = `${name}.${index}`

  const nomeField = `${nameIndex}.pessoaReferencia.nome` as Path<TFieldValues>
  const dataNascimentoField = `${nameIndex}.pessoaReferencia.dataNascimento` as Path<TFieldValues>
  const grauParentescoField = `${nameIndex}.tipoReferencia.id` as Path<TFieldValues>
  const profissaoField = `${nameIndex}.descricaoProfissao` as Path<TFieldValues>
  const rendaField = `${nameIndex}.descricaoRendaBeneficio` as Path<TFieldValues>
  const telefoneField = `${nameIndex}.telefone` as Path<TFieldValues>

  const enderecoField = `${nameIndex}.endereco`

  const enderecoFields = {
    cep: `${enderecoField}.cep` as Path<TFieldValues>,
    logradouro: `${enderecoField}.logradouro` as Path<TFieldValues>,
    bairro: `${enderecoField}.bairro.id` as Path<TFieldValues>,
    complemento: `${enderecoField}.complemento` as Path<TFieldValues>,
    numero: `${enderecoField}.numero` as Path<TFieldValues>,
    cidade: `${enderecoField}.cidade.id` as Path<TFieldValues>,
    uf: `${enderecoField}.cidade.uf.id` as Path<TFieldValues>,
  }

  const telefoneWatch = watch(telefoneField)

  useEffect(() => {
    if (telefoneWatch) {
      const value = phoneMask(String(telefoneWatch)) as FieldPathValue<
        TFieldValues,
        FieldPath<TFieldValues>
      >
      setValue(telefoneField, value)
    }
  }, [telefoneWatch])

  const handleDeleteDialog = () => {
    return new Promise((resolve, reject) => {
      try {
        onDelete(index)
      } catch (error) {
        reject(error)
      }
    }).finally(() => setOpenExcluded(false))
  }

  return (
    <Box display={'flex'} flexDirection={'row'} alignItems={'start'} gap={2}>
      <Grid container item spacing={2} xs={12}>
        <Grid item md={4} xs={12}>
          <FormInputAutoComplete
            control={control}
            name={grauParentescoField}
            label='Grau de Parentesco'
            options={familiarReferenceOptions}
            disabled={disabled}
            getOptionLabel={(option) => option.label}
            getOptionById={(id: string | null, options: ItemOption[]) =>
              options.find((option) => Number(option.value) === Number(id)) || null
            }
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            onChangeValue={(option) => Number(option?.value) || null}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <FormInput control={control} name={nomeField} label={'Nome'} disabled={disabled} />
        </Grid>
        <Grid item md={4} xs={12}>
          <FormInputDatePicker
            control={control}
            name={dataNascimentoField}
            label={'Data de Nascimento'}
            disabled={disabled}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <FormInput
            control={control}
            name={profissaoField}
            label={'Profissão/Ocupação'}
            disabled={disabled}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <FormInput
            control={control}
            name={rendaField}
            label={'Renda/Benefício'}
            disabled={disabled}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <FormInput
            control={control}
            name={telefoneField}
            label={'Telefone'}
            disabled={disabled}
            maxLength={15}
          />
        </Grid>
        {hasAddress && (
          <Grid item xs={12}>
            <FormAddress
              control={control}
              watch={watch}
              setValue={setValue}
              fields={enderecoFields}
              disabled={disabled}
            />
          </Grid>
        )}
      </Grid>
      {showDeleteButton && (
        <Tooltip title='Excluir'>
          <IconButton color={'error'} onClick={() => setOpenExcluded(true)} disabled={disabled}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      )}
      <DialogAlert
        title='Excluir'
        contentText={labels.labelDeleteItem}
        open={openExcluded}
        onSuccess={handleDeleteDialog}
        onClose={() => setOpenExcluded(false)}
      />
    </Box>
  )
}
